import { ChevronDown, ChevronUp, Download, TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useSidebar } from "@/components/layout/SidebarContext";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { isActive, useZipJobs } from "@/hooks/use-zips";
import {
  checkDownload,
  DOWNLOAD_EXPIRED,
  downloadsErrorMessage,
  isSignedOut,
} from "@/lib/zip-download";
import type { ZipJob } from "@/schemas/zips";

/** Finished or failed jobs shown below the ones still being built, which are always shown. */
const RECENT_SHOWN = 3;

/** "1 photo", "12 photos", "3 of 12 photos": the noun follows the total. */
function photosText(count: number, total = count): string {
  const noun = total === 1 ? "photo" : "photos";
  return count === total
    ? `${total.toLocaleString("en-US")} ${noun}`
    : `${count.toLocaleString("en-US")} of ${total.toLocaleString("en-US")} ${noun}`;
}

function countText(job: ZipJob): string {
  return job.missingCount > 0
    ? photosText(job.totalCount - job.missingCount, job.totalCount)
    : photosText(job.totalCount);
}

/**
 * Asks the server first, so a closed file (a share withdrawn, a job gone) is a message in the
 * app and not a download that silently fails. Then lets the browser fetch it.
 */
async function startDownload(job: ZipJob): Promise<void> {
  const url = `/api/zips/${encodeURIComponent(job.id)}/file`;
  const problem = await checkDownload(url);
  if (problem) return void toast.error(problem);
  window.location.assign(url);
}

function JobRow({ job }: { job: ZipJob }) {
  const percent = job.totalCount > 0 ? Math.round((job.doneCount / job.totalCount) * 100) : 0;
  return (
    <li data-testid="download-job" data-status={job.status} className="space-y-1.5 px-3 py-2.5">
      <p className="truncate text-sm font-medium" title={job.fileName}>
        {job.fileName}
      </p>
      {isActive(job) && (
        <>
          <div
            role="progressbar"
            aria-label={`Preparing ${job.fileName}`}
            aria-valuemin={0}
            aria-valuemax={job.totalCount}
            aria-valuenow={job.doneCount}
            className="h-1.5 overflow-hidden rounded-full bg-muted"
          >
            <div
              className="h-full rounded-full bg-(--brand-accent) transition-[width] duration-500"
              style={{ width: `${percent}%` }}
            />
          </div>
          <p data-testid="download-progress" className="text-xs text-muted-foreground tabular-nums">
            {job.status === "queued"
              ? "Waiting for its turn"
              : `${job.doneCount.toLocaleString("en-US")} of ${photosText(job.totalCount)}`}
          </p>
        </>
      )}
      {job.status === "done" && (
        <div className="flex items-center justify-between gap-2">
          <p data-testid="download-summary" className="text-xs text-muted-foreground tabular-nums">
            {countText(job)}, ready
          </p>
          <Button
            size="sm"
            variant="outline"
            data-testid="download-link"
            onClick={() => void startDownload(job)}
          >
            <Download className="size-4" />
            Download
          </Button>
        </div>
      )}
      {job.status === "expired" && (
        <p data-testid="download-expired" className="text-xs text-muted-foreground">
          {DOWNLOAD_EXPIRED}
        </p>
      )}
      {job.status === "failed" && (
        <p className="flex items-start gap-1.5 text-xs text-destructive">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          <span>{job.error ?? "This download failed."}</span>
        </p>
      )}
    </li>
  );
}

interface TrayViewProps {
  jobs: ZipJob[];
  error: unknown;
  hasError: boolean;
  open: boolean;
  onToggle: () => void;
  onRetry: () => void;
}

/**
 * The bar sits in the page's own column, under the content, so it never covers a control. The
 * list is different: it floats above the bar, over the bottom right of the page, and covers
 * whatever is there until it is closed. A load failure shows in the bar whether the list is open
 * or not.
 */
export function DownloadsTrayView({
  jobs,
  error,
  hasError,
  open,
  onToggle,
  onRetry,
}: TrayViewProps) {
  const activeCount = jobs.filter(isActive).length;
  const shown = [
    ...jobs.filter(isActive),
    ...jobs.filter((job) => !isActive(job)).slice(0, RECENT_SHOWN),
  ];
  const Chevron = open ? ChevronDown : ChevronUp;

  return (
    <section
      data-testid="downloads-tray"
      aria-label="Downloads"
      className="relative flex shrink-0 flex-col items-end gap-1 border-t bg-background px-4 py-1.5"
    >
      {hasError && (
        <div role="alert" data-testid="downloads-error" className="w-full">
          <ErrorState
            compact
            message={downloadsErrorMessage(error)}
            onRetry={isSignedOut(error) ? undefined : onRetry}
          />
        </div>
      )}
      {open && (
        <div
          data-testid="downloads-list"
          className="absolute right-4 bottom-full z-40 mb-1 max-h-[50vh] w-[min(22rem,calc(100vw-2rem))] overflow-y-auto rounded-lg border bg-card shadow-lg"
        >
          {shown.length === 0 ? (
            <p className="px-3 py-2.5 text-xs text-muted-foreground">No downloads yet.</p>
          ) : (
            <ul className="divide-y">
              {shown.map((job) => (
                <JobRow key={job.id} job={job} />
              ))}
            </ul>
          )}
        </div>
      )}
      <Button
        variant="ghost"
        size="sm"
        aria-expanded={open}
        onClick={onToggle}
        className="h-8 gap-2 px-2"
      >
        <span className="text-sm font-semibold">
          Downloads
          {activeCount > 0 && (
            <span className="ml-2 font-normal text-muted-foreground">
              {activeCount} in progress
            </span>
          )}
        </span>
        <Chevron className="size-4" />
      </Button>
    </section>
  );
}

/**
 * On a wide screen a new download opens the list, so it is not missed. On a phone the list would
 * cover most of the screen, so it stays closed: the bar says "1 in progress" and a toast says when
 * it is ready.
 */
export function opensForNewDownload(active: number, previous: number, isMobile: boolean): boolean {
  return active > previous && !isMobile;
}

/**
 * Every download this account asked for, in the layout of every page, so it keeps working while
 * the person moves around the app. The bar shows once the account has a download. The list is
 * closed until a download starts on a wide screen, or the person opens it.
 */
export function DownloadsTray() {
  const { data: jobs, isError, error, refetch } = useZipJobs();
  const { isMobile } = useSidebar();
  const [open, setOpen] = useState(false);
  const seen = useRef<Map<string, ZipJob["status"]> | null>(null);
  const previousActive = useRef(0);
  const activeCount = jobs?.filter(isActive).length ?? 0;

  // A download that was being built and is now ready is worth a nudge, wherever the person is.
  // Jobs in the first list are old news; a small one can go from asked for to done between polls.
  useEffect(() => {
    if (!jobs) return;
    const known = seen.current;
    const statuses = known ?? new Map<string, ZipJob["status"]>();
    seen.current = statuses;
    for (const job of jobs) {
      const before = statuses.get(job.id);
      if (known && before !== "done" && job.status === "done") {
        toast.success(
          job.missingCount > 0
            ? `${job.fileName} is ready, with ${countText(job)}`
            : `${job.fileName} is ready`,
        );
      }
      statuses.set(job.id, job.status);
    }
  }, [jobs]);

  // A list the person closed stays closed until another download starts.
  useEffect(() => {
    if (opensForNewDownload(activeCount, previousActive.current, isMobile)) setOpen(true);
    previousActive.current = activeCount;
  }, [activeCount, isMobile]);

  // Nothing loaded and nothing wrong: stay out of the way. A failed load is shown, not hidden.
  if (!jobs && !isError) return null;
  if (jobs && jobs.length === 0 && !isError) return null;

  return (
    <DownloadsTrayView
      jobs={jobs ?? []}
      error={error}
      hasError={isError}
      open={open}
      onToggle={() => setOpen(!open)}
      onRetry={() => void refetch()}
    />
  );
}
